"use client";

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useRouter } from "@/lib/navigation";
import {
  LayoutDashboard,
  ClipboardList,
  AlertTriangle,
  Ticket,
  CheckSquare,
  Megaphone,
  Plug,
  Settings,
  Shield,
  Building2,
  Users,
  UsersRound,
  Workflow,
  ListChecks,
  FileStack,
  KeyRound,
  DashboardLayout,
  PermissionGate,
  usePermissions,
  useEntitlements,
  DropdownMenuItem,
  DropdownMenuSeparator,
  type NavItem,
} from "@repo/ui";
import { useApiClient, useSession } from "@repo/api-client";
import { useMessages } from "@/locales";
import { PortalNotificationBell } from "@/components/notification-bell";
import { LanguageSwitcher } from "@/components/language-switcher";
import { WorkspaceSwitcher } from "@/components/workspace-switcher";

const ADMIN_HUB_PERMS = [
  "user:read",
  "client_organization:read",
  "store:read",
  "setting:read",
  "workflow_template:read",
  "document_type:read",
  "comment_preset:read",
  "service_type:read",
  "team:manage",
  "team:member_manage",
  "role:read",
  "billing_account:read",
  "audit:read",
] as const;

export default function DashboardShellLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const m = useMessages();

  const router = useRouter();
  const pathname = usePathname();
  const { user, accessToken, logout, workspaceId } = useSession();
  const api = useApiClient();
  const { has } = usePermissions();
  const { included } = useEntitlements();
  const tasksCount = useQuery({
    queryKey: ["sidebar-tasks-count", workspaceId],
    queryFn: () => api.logistics.orderServices.list({ page: 1, pageSize: 1, filter: "my_tasks", logisticsBranchId: workspaceId ?? undefined }),
    enabled: Boolean(accessToken) && included("feature.module.logistics-workflow"),
    staleTime: 30_000,
  });
  const exceptionsCount = useQuery({
    queryKey: ["sidebar-exceptions-count", workspaceId],
    queryFn: () => api.logistics.exceptions({ page: 1, pageSize: 1, logisticsBranchId: workspaceId ?? undefined }),
    enabled: Boolean(accessToken) && included("feature.module.logistics-workflow"),
    staleTime: 30_000,
  });
  React.useEffect(() => {
    if (!accessToken) router.replace("/login");
  }, [accessToken, router]);

  React.useEffect(() => {
    if (!included("feature.module.billing_accounts") && pathname.startsWith("/admin/billing")) router.replace("/admin");
  }, [included, pathname, router]);

  // Wait for session bootstrap: empty permissions would hide every PermissionGate
  // (blank main pane) while empty entitlements incorrectly treat all features as on.
  if (
    !accessToken ||
    !user ||
    (!included("feature.module.billing_accounts") &&
      pathname.startsWith("/admin/billing"))
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        …
      </div>
    );
  }

  const onAdminCatalog =
    pathname.startsWith("/admin") &&
    !pathname.startsWith("/admin/organizations") &&
    !pathname.startsWith("/admin/roles") &&
    !pathname.startsWith("/admin/teams") &&
    !pathname.startsWith("/admin/workflows");

  const operationsItems: NavItem[] = [
    {
      id: "home",
      label: m.nav.home,
      href: "/",
      active: pathname === "/",
      icon: <LayoutDashboard strokeWidth={1.6} />,
    },
    ...(included("feature.module.logistics-workflow") ? [{
      id: "order-services",
      label: m.nav.orderServices,
      href: "/order-services",
      active: pathname.startsWith("/order-services"),
      icon: <ClipboardList strokeWidth={1.6} />,
    } satisfies NavItem,
    ...(has("billing_upload:use")
      ? [{
          id: "billing-upload",
          label: m.nav.billingUpload,
          href: "/billing-upload",
          active: pathname.startsWith("/billing-upload"),
          icon: <FileStack strokeWidth={1.6} />,
        } satisfies NavItem]
      : []),
    {
      id: "tasks",
      label: m.nav.tasks,
      href: "/tasks",
      active: pathname.startsWith("/tasks"),
      icon: <ListChecks strokeWidth={1.6} />,
      count: tasksCount.data?.total,
    } satisfies NavItem,
    {
      id: "exceptions",
      label: m.nav.exceptions,
      href: "/exceptions",
      active: pathname.startsWith("/exceptions"),
      icon: <AlertTriangle strokeWidth={1.6} />,
      alert: true,
      count: exceptionsCount.data?.total,
    } satisfies NavItem] : []),
  ];

  const inboxItems: NavItem[] = [
    ...(included("feature.module.logistics-workflow") ? [{
      id: "approvals",
      label: m.nav.approvals,
      href: "/approvals",
      active: pathname.startsWith("/approvals"),
      icon: <CheckSquare strokeWidth={1.6} />,
    } satisfies NavItem] : []),
    ...(included("feature.module.ticketing") ? [{
      id: "tickets",
      label: m.nav.tickets,
      href: "/tickets",
      active: pathname.startsWith("/tickets"),
      icon: <Ticket strokeWidth={1.6} />,
    } satisfies NavItem] : []),
    ...(included("feature.module.announcement")
      ? [
          {
            id: "announcements",
            label: m.nav.announcements,
            href: "/announcements",
            active: pathname.startsWith("/announcements"),
            icon: <Megaphone strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
  ];

  const administrationItems: NavItem[] = [
    ...(ADMIN_HUB_PERMS.some((p) => has(p))
      ? [
          {
            id: "admin-overview",
            label: m.nav.adminOverview,
            href: "/admin",
            active: pathname === "/admin" || onAdminCatalog,
            icon: <Shield strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(included("feature.module.logistics-workflow") && has("client_organization:read")
      ? [
          {
            id: "organizations",
            label: m.nav.organizations,
            href: "/admin/organizations",
            active: pathname.startsWith("/admin/organizations"),
            icon: <Building2 strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(has("user:read")
      ? [
          {
            id: "users",
            label: m.nav.users,
            href: "/users",
            active: pathname.startsWith("/users"),
            icon: <Users strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(has("role:read")
      ? [
          {
            id: "roles",
            label: m.nav.roles,
            href: "/admin/roles",
            active: pathname.startsWith("/admin/roles"),
            icon: <KeyRound strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(has("team:manage") || has("team:member_manage") || has("role:read")
      ? [
          {
            id: "teams",
            label: m.nav.teams,
            href: "/admin/teams",
            active: pathname.startsWith("/admin/teams"),
            icon: <UsersRound strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(included("feature.module.logistics-workflow") && has("workflow_template:read")
      ? [
          {
            id: "workflows",
            label: m.nav.workflows,
            href: "/admin/workflows",
            active: pathname.startsWith("/admin/workflows"),
            icon: <Workflow strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
  ];

  const systemItems: NavItem[] = [
    ...(included("feature.module.connections")
      ? [
          {
            id: "connections",
            label: m.nav.connections,
            href: "/connections",
            active: pathname.startsWith("/connections"),
            icon: <Plug strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
    ...(has("setting:read")
      ? [
          {
            id: "settings",
            label: m.nav.settings,
            href: "/settings",
            active: pathname.startsWith("/settings"),
            icon: <Settings strokeWidth={1.6} />,
          } satisfies NavItem,
        ]
      : []),
  ];

  const navGroups = [
    {
      id: "operations",
      label: m.shell.navOperations,
      items: operationsItems,
    },
    {
      id: "inbox",
      label: m.shell.navInbox,
      items: inboxItems,
    },
    ...(administrationItems.length > 0
      ? [
          {
            id: "administration",
            label: m.shell.navAdministration,
            items: administrationItems,
          },
        ]
      : []),
    ...(systemItems.length > 0
      ? [
          {
            id: "system",
            label: m.shell.navSystem,
            items: systemItems,
          },
        ]
      : []),
  ];

  const flatNav = navGroups.flatMap((g) =>
    g.items.map((item) => ({ ...item, groupLabel: g.label })),
  );
  const active = flatNav.find((n) => n.active);
  const activeSection =
    navGroups.find((g) => g.items.some((n) => n.active))?.label ??
    m.shell.navOperations;

  return (
    <DashboardLayout
      brandMark={
        <img
          src="/favicon.png"
          alt=""
          className="size-full object-contain"
        />
      }
      brandMarkTransparent
      brand={<span>{m.appName}</span>}
      brandSubtitle={m.appSubtitle}
      navGroups={navGroups}
      pageCrumb={{
        section: activeSection,
        page: active?.label ?? m.nav.home,
      }}
      onNavigate={(href) => router.push(href)}
      workspaceSwitcher={
        <WorkspaceSwitcher label={m.shell.branchLabel} variant="sidebar" />
      }
      headerExtras={<LanguageSwitcher className="h-8 w-[7.5rem] text-xs" />}
      notificationBell={included("feature.module.notification") ? <PortalNotificationBell /> : null}
      searchPlaceholder={m.shell.searchPlaceholder}
      profile={{
        name: user?.name ?? user?.email ?? "",
        role: m.shell.staffRoleLabel,
      }}
      commandLabels={{
        defaultGroup: m.common.navGroup,
        placeholder: m.common.goTo,
        empty: m.common.noCommands,
      }}
      commandItems={flatNav.map((n) => ({
        id: n.id,
        label: n.label,
        href: n.href,
        group: n.groupLabel,
      }))}
      a11yLabels={{
        mainNav: m.common.navGroup,
        openMenu: m.common.openMenu,
        closeMenu: m.common.closeMenu,
        close: m.common.close,
        toggleSidebar: m.shell.toggleSidebar,
        search: m.shell.searchAria,
        themeLight: m.shell.themeLight,
        themeDark: m.shell.themeDark,
        profileMenu: m.shell.profileMenu,
      }}
      userMenu={
        <>
          <div className="px-2 py-1.5">
            <p className="truncate text-sm font-medium">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {user?.email}
            </p>
          </div>
          <DropdownMenuSeparator />
          <PermissionGate permission="setting:read">
            <DropdownMenuItem
              onSelect={() => router.push("/settings")}
              className="cursor-pointer"
            >
              {m.nav.settings}
            </DropdownMenuItem>
          </PermissionGate>
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:text-destructive"
            onSelect={() => {
              void logout().then(() => router.replace("/login"));
            }}
          >
            {m.logout}
          </DropdownMenuItem>
        </>
      }
    >
      {children}
    </DashboardLayout>
  );
}
